// ========================= PART 1 / 4 =========================
// src/Room.js

import React, { useEffect, useMemo, useRef, useState } from "react";
import io from "socket.io-client";

const DEBUG = true;
const suppressOverlay = () => {
    const noop = () => {};

    window.addEventListener("error", (event) => {
        event.preventDefault();
        event.stopPropagation();
        return true;
    });

    window.addEventListener("unhandledrejection", (event) => {
        event.preventDefault();
        event.stopPropagation();
        return true;
    });

    console.error = noop;
    console.warn = noop;
};
suppressOverlay();

const SIGNALING_URL =
    process.env.REACT_APP_SIGNALING_URL || window.location.origin;

const iceServers = [
    { urls: ["stun:stun.l.google.com:19302"] },
    {
        urls: [
            "turn:xcam.irantoitaly.com:3478?transport=udp",
            "turn:xcam.irantoitaly.com:3478?transport=tcp",
        ],
        username: "webrtcuser",
        credential: "StrongPasswordHere",
    },
];

const emptyStats = {
    bytesSent: 0,
    bytesReceived: 0,
    uploadKbps: 0,
    downloadKbps: 0,

    packetsSent: 0,
    packetsReceived: 0,
    packetsLost: 0,
    packetLossPercent: 0,

    jitter: 0,
    rtt: 0,

    fps: 0,
    width: 0,
    height: 0,

    framesSent: 0,
    framesReceived: 0,
    framesDropped: 0,
    framesDecoded: 0,

    freezeCount: 0,
    pauseCount: 0,

    nackCount: 0,
    pliCount: 0,
    firCount: 0,

    outgoingKbps: 0,
    incomingKbps: 0,

    candidatePair: "—",
    usingTurn: false,
    realMediaFlow: false,

    quality: "Waiting",
    score: 0,
};

const log = (...args) => {
    if (DEBUG) console.log("[WEBRTC]", ...args);
};

const warn = (...args) => {
    if (DEBUG) console.warn("[WEBRTC]", ...args);
};

function avg(arr, key) {
    return arr.length
        ? arr.reduce((sum, item) => sum + Number(item[key] || 0), 0) / arr.length
        : 0;
}

function max(arr, key) {
    return arr.length
        ? Math.max(...arr.map((item) => Number(item[key] || 0)))
        : 0;
}

function formatBytes(bytes) {
    if (!bytes) return "0 B";

    const units = ["B", "KB", "MB", "GB"];
    const index = Math.min(
        Math.floor(Math.log(bytes) / Math.log(1024)),
        units.length - 1
    );

    return `${(bytes / Math.pow(1024, index)).toFixed(2)} ${units[index]}`;
}

function getQuality(stats) {
    if (!stats.realMediaFlow) return "No Media";

    if (
        stats.packetLossPercent > 5 ||
        stats.jitter > 80 ||
        stats.rtt > 450 ||
        stats.freezeCount > 3
    ) {
        return "Poor";
    }

    if (
        stats.packetLossPercent > 2 ||
        stats.jitter > 40 ||
        stats.rtt > 250 ||
        stats.framesDropped > 20
    ) {
        return "Fair";
    }

    if (stats.fps && stats.fps < 15) {
        return "Weak";
    }

    return "Good";
}

function getScore(stats) {
    if (!stats.realMediaFlow) return 0;

    let score = 100;

    score -= Math.min(35, stats.packetLossPercent * 7);
    score -= Math.min(25, stats.jitter / 4);
    score -= Math.min(20, stats.rtt / 25);
    score -= Math.min(15, stats.framesDropped / 3);
    score -= Math.min(10, stats.freezeCount * 3);

    return Math.max(0, Math.round(score));
}

export default function Room({ roomId }) {
    const [socketId, setSocketId] = useState(null);
    const [otherUserId, setOtherUserId] = useState(null);

    const [connectionState, setConnectionState] = useState("new");
    const [iceState, setIceState] = useState("new");
    const [signalingState, setSignalingState] = useState("stable");

    const [stats, setStats] = useState(emptyStats);
    const [history, setHistory] = useState([]);
    const [events, setEvents] = useState([]);
    const [hiccups, setHiccups] = useState([]);
    const [losses, setLosses] = useState([]);

    const [isCapturing, setIsCapturing] = useState(false);
    const [localMode, setLocalMode] = useState("camera");
    const [fullscreenTarget, setFullscreenTarget] = useState(null);

    const [microphones, setMicrophones] = useState([]);
    const [selectedMicId, setSelectedMicId] = useState("");

    const socketRef = useRef(null);
    const pcRef = useRef(null);

    const localStreamRef = useRef(null);
    const remoteStreamRef = useRef(null);
    const lastLocalStreamRef = useRef(null);

    const pendingCandidatesRef = useRef([]);
    const remoteIdRef = useRef(null);

    const statsTimerRef = useRef(null);
    const reconnectTimerRef = useRef(null);
    const reconnectAttemptRef = useRef(0);

    const cutStartRef = useRef(null);

    const historyRef = useRef([]);
    const isCapturingRef = useRef(false);
    const localModeRef = useRef("camera");

    const lastStatsRef = useRef({
        timestamp: 0,
        bytesSent: 0,
        bytesReceived: 0,
    });

    const lastRemoteFramesRef = useRef(0);

    const localVideoRef = useRef(null);
    const remoteVideoRef = useRef(null);
    const fullscreenVideoRef = useRef(null);

    useEffect(() => {
        isCapturingRef.current = isCapturing;
    }, [isCapturing]);

    useEffect(() => {
        localModeRef.current = localMode;
    }, [localMode]);

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                setFullscreenTarget(null);
            }
        };

        window.addEventListener("keydown", onKeyDown);

        return () => {
            window.removeEventListener("keydown", onKeyDown);
        };
    }, []);

    useEffect(() => {
        if (fullscreenTarget) {
            document.body.style.overflow = "hidden";
            document.documentElement.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
            document.documentElement.style.overflow = "";
        }

        return () => {
            document.body.style.overflow = "";
            document.documentElement.style.overflow = "";
        };
    }, [fullscreenTarget]);

    useEffect(() => {
        if (!fullscreenVideoRef.current || !fullscreenTarget) return;

        fullscreenVideoRef.current.srcObject =
            fullscreenTarget === "local" ? localStreamRef.current : remoteStreamRef.current;
    }, [fullscreenTarget]);

    const addEvent = (type, detail, extra = {}) => {
        if (!isCapturingRef.current && !type.includes("CAPTURE")) return;

        const item = {
            id: `${Date.now()}-${Math.random()}`,
            time: new Date().toLocaleTimeString(),
            timestamp: Date.now(),
            type,
            detail,
            ...extra,
        };

        log(type, detail, extra);

        setEvents((previous) => [item, ...previous].slice(0, 500));
    };

    const addHiccup = (type, detail, severity = "medium") => {
        if (!isCapturingRef.current) return;

        const item = {
            id: `${Date.now()}-${Math.random()}`,
            time: new Date().toLocaleTimeString(),
            timestamp: Date.now(),
            type,
            detail,
            severity,
        };

        warn("HICCUP", item);

        setHiccups((previous) => [item, ...previous].slice(0, 500));
    };

    const refreshMicrophones = async () => {
        try {
            await navigator.mediaDevices.getUserMedia({
                audio: true,
                video: false,
            });

            const devices = await navigator.mediaDevices.enumerateDevices();

            const audioInputs = devices.filter((device) => device.kind === "audioinput");

            setMicrophones(audioInputs);

            if (!selectedMicId && audioInputs.length > 0) {
                setSelectedMicId(audioInputs[0].deviceId);
            }
        } catch (error) {
            console.warn("Could not enumerate microphones:", error);
            addEvent("MIC ERROR", error.message);
        }
    };

    useEffect(() => {
        refreshMicrophones();

        navigator.mediaDevices?.addEventListener?.("devicechange", refreshMicrophones);

        return () => {
            navigator.mediaDevices?.removeEventListener?.("devicechange", refreshMicrophones);
        };
    }, []);

    const getAudioConstraint = () => {
        if (!selectedMicId) {
            return {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
            };
        }

        return {
            deviceId: { exact: selectedMicId },
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
        };
    };

    const requestCamera = async () => {
        return navigator.mediaDevices.getUserMedia({
            video: {
                width: { ideal: 1280 },
                height: { ideal: 720 },
                frameRate: { ideal: 30, max: 30 },
            },
            audio: getAudioConstraint(),
        });
    };

    const requestMicOnly = async () => {
        return navigator.mediaDevices.getUserMedia({
            audio: getAudioConstraint(),
            video: false,
        });
    };

    const requestScreenFromButton = async () => {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
            video: {
                cursor: "always",
            },
            audio: true,
        });

        const micStream = await requestMicOnly();

        const mixedStream = new MediaStream([
            ...screenStream.getVideoTracks(),
            ...micStream.getAudioTracks(),
        ]);

        screenStream.getVideoTracks().forEach((track) => {
            track.onended = () => {
                mixedStream.getAudioTracks().forEach((audioTrack) => audioTrack.stop());
                switchToCamera();
            };
        });

        return mixedStream;
    };
    // ========================= PART 2 / 4 =========================

    const attachLocalStream = (stream, mode) => {
        localStreamRef.current = stream;
        lastLocalStreamRef.current = stream;

        if (localVideoRef.current) {
            localVideoRef.current.srcObject = stream;
        }

        if (fullscreenTarget === "local" && fullscreenVideoRef.current) {
            fullscreenVideoRef.current.srcObject = stream;
        }

        stream.getTracks().forEach((track) => {
            track.onended = () => {
                addEvent("LOCAL TRACK ENDED", `${track.kind} ended.`);

                if (track.kind === "video" && mode === "screen") {
                    localStreamRef.current?.getAudioTracks().forEach((audioTrack) => {
                        audioTrack.stop();
                    });

                    setLocalMode("camera");
                    localModeRef.current = "camera";

                    switchToCamera();
                }
            };

            track.onmute = () => {
                addHiccup("LOCAL TRACK MUTED", `${track.kind} muted.`, "medium");
            };

            track.onunmute = () => {
                addEvent("LOCAL TRACK UNMUTED", `${track.kind} unmuted.`);
            };
        });
    };

    const getCurrentLocalStream = async () => {
        if (localStreamRef.current) {
            return localStreamRef.current;
        }

        const stream = await requestCamera();

        attachLocalStream(stream, "camera");

        setLocalMode("camera");
        localModeRef.current = "camera";

        return stream;
    };

    const replaceTracks = async (newStream, mode) => {
        const pc = pcRef.current;

        if (!pc) {
            localStreamRef.current?.getTracks().forEach((track) => {
                track.stop();
            });

            attachLocalStream(newStream, mode);

            setLocalMode(mode);
            localModeRef.current = mode;

            return;
        }

        const senders = pc.getSenders();

        const newVideoTrack = newStream.getVideoTracks()[0];
        const newAudioTrack = newStream.getAudioTracks()[0];

        const videoSender = senders.find((sender) => sender.track?.kind === "video");
        const audioSender = senders.find((sender) => sender.track?.kind === "audio");

        if (videoSender && newVideoTrack) {
            await videoSender.replaceTrack(newVideoTrack);
        }

        if (audioSender && newAudioTrack) {
            await audioSender.replaceTrack(newAudioTrack);
        }

        localStreamRef.current?.getTracks().forEach((track) => {
            track.stop();
        });

        attachLocalStream(newStream, mode);

        setLocalMode(mode);
        localModeRef.current = mode;

        addEvent(mode === "screen" ? "SCREEN SHARE STARTED" : "CAMERA STARTED", mode);
    };

    const switchToCamera = async () => {
        const stream = await requestCamera();

        await replaceTracks(stream, "camera");
    };

    const switchToScreen = async () => {
        try {
            const stream = await requestScreenFromButton();

            await replaceTracks(stream, "screen");
        } catch (error) {
            addEvent("SCREEN SHARE CANCELLED", error.message);
            console.warn("Screen share failed or cancelled:", error);
        }
    };

    const switchMicrophone = async (deviceId) => {
        setSelectedMicId(deviceId);

        const pc = pcRef.current;

        if (!pc || !localStreamRef.current) return;

        try {
            const micStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    deviceId: { exact: deviceId },
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
                video: false,
            });

            const newAudioTrack = micStream.getAudioTracks()[0];

            const audioSender = pc.getSenders().find((sender) => {
                return sender.track?.kind === "audio";
            });

            if (audioSender && newAudioTrack) {
                await audioSender.replaceTrack(newAudioTrack);
            }

            localStreamRef.current.getAudioTracks().forEach((track) => {
                track.stop();
            });

            localStreamRef.current.addTrack(newAudioTrack);

            addEvent("MICROPHONE CHANGED", `Selected microphone deviceId: ${deviceId}`);
        } catch (error) {
            addEvent("MIC SWITCH ERROR", error.message);
        }
    };

    const flushPendingCandidates = async () => {
        const pc = pcRef.current;

        if (!pc || !pc.remoteDescription) return;

        for (const candidate of pendingCandidatesRef.current) {
            try {
                await pc.addIceCandidate(candidate);
            } catch (error) {
                addEvent("ICE ADD ERROR", error.message);
            }
        }

        pendingCandidatesRef.current = [];
    };

    const startCapture = () => {
        setStats(emptyStats);
        setHistory([]);
        setEvents([]);
        setHiccups([]);
        setLosses([]);

        historyRef.current = [];

        lastStatsRef.current = {
            timestamp: 0,
            bytesSent: 0,
            bytesReceived: 0,
        };

        lastRemoteFramesRef.current = 0;

        isCapturingRef.current = true;
        setIsCapturing(true);

        addEvent(
            "CAPTURE STARTED",
            "مانیتورینگ شروع شد. آمار، قطعی‌ها، هیکاپ‌ها و کیفیت واقعی اتصال ثبت می‌شود."
        );

        startStatsMonitor();
    };

    const stopCapture = () => {
        isCapturingRef.current = false;
        setIsCapturing(false);

        stopStatsMonitor();

        addEvent("CAPTURE STOPPED", "مانیتورینگ متوقف شد.");
    };

    const stopStatsMonitor = () => {
        if (statsTimerRef.current) {
            clearInterval(statsTimerRef.current);
        }

        statsTimerRef.current = null;
    };

    const startStatsMonitor = () => {
        stopStatsMonitor();

        statsTimerRef.current = setInterval(async () => {
            const pc = pcRef.current;

            if (!pc || !isCapturingRef.current) return;

            try {
                const reports = await pc.getStats();

                const next = { ...emptyStats };

                let localType = "—";
                let remoteType = "—";
                let localProto = "—";
                let remoteProto = "—";

                reports.forEach((report) => {
                    if (report.type === "outbound-rtp" && report.kind === "video") {
                        next.bytesSent += report.bytesSent || 0;
                        next.packetsSent += report.packetsSent || 0;
                        next.framesSent += report.framesSent || 0;

                        next.nackCount += report.nackCount || 0;
                        next.pliCount += report.pliCount || 0;
                        next.firCount += report.firCount || 0;

                        next.fps = report.framesPerSecond || next.fps;

                        next.width = report.frameWidth || next.width;
                        next.height = report.frameHeight || next.height;
                    }

                    if (report.type === "inbound-rtp" && report.kind === "video") {
                        next.bytesReceived += report.bytesReceived || 0;

                        next.packetsReceived += report.packetsReceived || 0;
                        next.packetsLost += report.packetsLost || 0;

                        next.framesReceived += report.framesReceived || 0;
                        next.framesDropped += report.framesDropped || 0;
                        next.framesDecoded += report.framesDecoded || 0;

                        next.freezeCount += report.freezeCount || 0;
                        next.pauseCount += report.pauseCount || 0;

                        next.nackCount += report.nackCount || 0;
                        next.pliCount += report.pliCount || 0;
                        next.firCount += report.firCount || 0;

                        next.jitter = Math.max(next.jitter, (report.jitter || 0) * 1000);

                        next.fps = report.framesPerSecond || next.fps;

                        next.width = report.frameWidth || next.width;
                        next.height = report.frameHeight || next.height;
                    }

                    if (
                        report.type === "candidate-pair" &&
                        report.state === "succeeded" &&
                        (report.nominated || report.selected)
                    ) {
                        next.rtt = (report.currentRoundTripTime || 0) * 1000;

                        next.outgoingKbps = (report.availableOutgoingBitrate || 0) / 1000;
                        next.incomingKbps = (report.availableIncomingBitrate || 0) / 1000;

                        const local = reports.get(report.localCandidateId);
                        const remote = reports.get(report.remoteCandidateId);

                        localType = local?.candidateType || "—";
                        remoteType = remote?.candidateType || "—";

                        localProto = local?.protocol || "—";
                        remoteProto = remote?.protocol || "—";
                    }
                });

                const now = Date.now();
                const last = lastStatsRef.current;

                const seconds = last.timestamp ? (now - last.timestamp) / 1000 : 1;

                next.uploadKbps = Math.max(
                    0,
                    ((next.bytesSent - last.bytesSent) * 8) / 1000 / seconds
                );

                next.downloadKbps = Math.max(
                    0,
                    ((next.bytesReceived - last.bytesReceived) * 8) / 1000 / seconds
                );

                const totalPackets = next.packetsReceived + next.packetsLost;

                next.packetLossPercent = totalPackets
                    ? (next.packetsLost / totalPackets) * 100
                    : 0;

                next.usingTurn = localType === "relay" || remoteType === "relay";

                next.candidatePair = `${localType}/${localProto} → ${remoteType}/${remoteProto}`;

                next.realMediaFlow =
                    pc.connectionState === "connected" &&
                    ["connected", "completed"].includes(pc.iceConnectionState) &&
                    next.framesReceived > lastRemoteFramesRef.current;

                next.quality = getQuality(next);
                next.score = getScore(next);

                next.time = new Date().toLocaleTimeString();
                next.timestamp = now;
                // ========================= PART 3 / 4 =========================

                const previous = historyRef.current[historyRef.current.length - 1];

                if (previous) {
                    if (next.framesReceived <= previous.framesReceived) {
                        addHiccup(
                            "REMOTE MEDIA STALLED",
                            "فریم دریافتی از کلاینت مقابل افزایش پیدا نکرده است.",
                            "high"
                        );
                    }

                    if (
                        next.packetLossPercent > 3 &&
                        previous.packetLossPercent <= 3
                    ) {
                        addHiccup(
                            "PACKET LOSS SPIKE",
                            `Packet loss: ${next.packetLossPercent.toFixed(2)}%`,
                            "high"
                        );
                    }

                    if (next.jitter > 60 && previous.jitter <= 60) {
                        addHiccup(
                            "JITTER SPIKE",
                            `Jitter: ${next.jitter.toFixed(1)} ms`,
                            "medium"
                        );
                    }

                    if (next.rtt > 350 && previous.rtt <= 350) {
                        addHiccup(
                            "HIGH RTT",
                            `RTT: ${next.rtt.toFixed(1)} ms`,
                            "medium"
                        );
                    }

                    if (next.freezeCount > previous.freezeCount) {
                        addHiccup(
                            "VIDEO FREEZE",
                            `${next.freezeCount - previous.freezeCount} new freeze(s).`,
                            "high"
                        );
                    }
                }

                lastRemoteFramesRef.current = next.framesReceived;

                historyRef.current = [...historyRef.current, next].slice(-1200);

                setStats(next);
                setHistory(historyRef.current);

                lastStatsRef.current = {
                    timestamp: now,
                    bytesSent: next.bytesSent,
                    bytesReceived: next.bytesReceived,
                };
            } catch (error) {
                addEvent("STATS ERROR", error.message);
            }
        }, 1000);
    };

    const exportCSV = (rows, name) => {
        if (!rows.length) return;

        const headers = Object.keys(rows[0]);

        const csv = [
            headers.join(","),
            ...rows.map((row) =>
                headers.map((header) => JSON.stringify(row[header] ?? "")).join(",")
            ),
        ].join("\n");

        const blob = new Blob([csv], {
            type: "text/csv;charset=utf-8;",
        });

        const url = window.URL.createObjectURL(blob);

        const anchor = document.createElement("a");

        anchor.href = url;
        anchor.download = `${name}_${Date.now()}.csv`;

        anchor.click();

        window.URL.revokeObjectURL(url);
    };

    const clearReconnectTimer = () => {
        if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
        }

        reconnectTimerRef.current = null;
    };

    const restartIce = async (reason) => {
        const pc = pcRef.current;
        const target = remoteIdRef.current;

        if (!pc || !target || !socketRef.current) return;

        if (pc.signalingState !== "stable") {
            addEvent(
                "ICE RESTART SKIPPED",
                `Signaling state is ${pc.signalingState}.`
            );
            return;
        }

        try {
            addEvent("ICE RESTARTING", reason);

            const offer = await pc.createOffer({
                iceRestart: true,
            });

            await pc.setLocalDescription(offer);

            socketRef.current.emit("offer", {
                target,
                sdp: offer,
                iceRestart: true,
            });
        } catch (error) {
            addEvent("ICE RESTART ERROR", error.message);
        }
    };

    const scheduleReconnectForever = (reason) => {
        clearReconnectTimer();

        reconnectTimerRef.current = setTimeout(async () => {
            const pc = pcRef.current;

            if (!pc) return;

            if (
                pc.connectionState === "connected" &&
                ["connected", "completed"].includes(pc.iceConnectionState)
            ) {
                reconnectAttemptRef.current = 0;
                return;
            }

            reconnectAttemptRef.current += 1;

            addEvent(
                "AUTO RECONNECT ATTEMPT",
                `${reason}. Attempt #${reconnectAttemptRef.current}.`
            );

            await restartIce(`Auto reconnect attempt #${reconnectAttemptRef.current}`);

            scheduleReconnectForever(reason);
        }, Math.min(3000 + reconnectAttemptRef.current * 1000, 10000));
    };

    // const stopStatsMonitor = () => {
    //     if (statsTimerRef.current) {
    //         clearInterval(statsTimerRef.current);
    //     }
    //
    //     statsTimerRef.current = null;
    // };

    const closePeerConnection = () => {
        stopStatsMonitor();
        clearReconnectTimer();

        try {
            pcRef.current?.close();
        } catch {}

        pcRef.current = null;
        pendingCandidatesRef.current = [];
    };

    const createPeerConnection = async (targetId) => {
        closePeerConnection();

        remoteIdRef.current = targetId;
        reconnectAttemptRef.current = 0;

        const pc = new RTCPeerConnection({
            iceServers,
            iceTransportPolicy: "all",
            bundlePolicy: "balanced",
            rtcpMuxPolicy: "require",
        });

        pcRef.current = pc;

        pc.onicecandidate = (event) => {
            if (!event.candidate) return;

            socketRef.current?.emit("ice-candidate", {
                target: targetId,
                candidate: event.candidate,
            });
        };

        pc.onicecandidateerror = (event) => {
            const message = `${event.url || "unknown"} | ${event.errorCode} | ${
                event.errorText || ""
            }`;

            if (event.errorCode === 701) {
                console.warn("ICE 701 warning:", message);
                addEvent("ICE 701 WARNING", message);
                return;
            }

            addEvent("ICE ERROR", message);
        };

        pc.onsignalingstatechange = () => {
            setSignalingState(pc.signalingState);
        };

        pc.onconnectionstatechange = () => {
            const state = pc.connectionState;

            setConnectionState(state);

            addEvent(`WEBRTC ${state.toUpperCase()}`, `Peer connection state: ${state}`);

            if (
                ["disconnected", "failed", "closed"].includes(state) &&
                !cutStartRef.current
            ) {
                cutStartRef.current = Date.now();

                addEvent("CONNECTION LOST", "اتصال واقعی بین دو کلاینت قطع شد.");

                scheduleReconnectForever(`Connection state is ${state}`);
            }

            if (state === "connected") {
                clearReconnectTimer();
                reconnectAttemptRef.current = 0;

                if (cutStartRef.current) {
                    const durationMs = Date.now() - cutStartRef.current;

                    setLosses((previous) => [
                        {
                            id: `${Date.now()}-${Math.random()}`,
                            time: new Date().toLocaleTimeString(),
                            timestamp: Date.now(),
                            durationMs,
                            reason: "WebRTC restored",
                        },
                        ...previous,
                    ]);

                    addEvent(
                        "CONNECTION RESTORED",
                        `اتصال بعد از ${(durationMs / 1000).toFixed(1)} ثانیه برگشت.`,
                        { outageMs: durationMs }
                    );

                    cutStartRef.current = null;
                }
            }

            if (state === "failed") {
                restartIce("Peer connection failed.");
                scheduleReconnectForever("Peer connection failed");
            }
        };

        pc.oniceconnectionstatechange = () => {
            const state = pc.iceConnectionState;

            setIceState(state);

            addEvent(`ICE ${state.toUpperCase()}`, `ICE state: ${state}`);

            if (["disconnected", "failed"].includes(state)) {
                if (!cutStartRef.current) {
                    cutStartRef.current = Date.now();

                    addEvent("ICE CONNECTION LOST", "مسیر ICE قطع شد.");
                }

                restartIce(`ICE state is ${state}`);
                scheduleReconnectForever(`ICE state is ${state}`);
            }

            if (state === "connected" || state === "completed") {
                clearReconnectTimer();
                reconnectAttemptRef.current = 0;

                if (cutStartRef.current) {
                    const durationMs = Date.now() - cutStartRef.current;

                    setLosses((previous) => [
                        {
                            id: `${Date.now()}-${Math.random()}`,
                            time: new Date().toLocaleTimeString(),
                            timestamp: Date.now(),
                            durationMs,
                            reason: "ICE restored",
                        },
                        ...previous,
                    ]);

                    addEvent(
                        "ICE RESTORED",
                        `ICE بعد از ${(durationMs / 1000).toFixed(1)} ثانیه برگشت.`,
                        { outageMs: durationMs }
                    );

                    cutStartRef.current = null;
                }
            }
        };

        pc.ontrack = (event) => {
            event.track.onmute = () => {
                addHiccup("REMOTE TRACK MUTED", `${event.track.kind} muted.`, "medium");
            };

            event.track.onunmute = () => {
                addEvent("REMOTE TRACK UNMUTED", `${event.track.kind} unmuted.`);
            };

            event.track.onended = () => {
                addEvent("REMOTE TRACK ENDED", `${event.track.kind} ended.`);
            };

            remoteStreamRef.current = event.streams[0];

            if (remoteVideoRef.current) {
                remoteVideoRef.current.srcObject = event.streams[0];
            }

            if (fullscreenTarget === "remote" && fullscreenVideoRef.current) {
                fullscreenVideoRef.current.srcObject = event.streams[0];
            }
        };

        const stream = await getCurrentLocalStream();

        stream.getTracks().forEach((track) => {
            pc.addTrack(track, stream);
        });

        return pc;
    };

    const startCall = async (targetId) => {
        const pc = await createPeerConnection(targetId);

        const offer = await pc.createOffer({
            offerToReceiveAudio: true,
            offerToReceiveVideo: true,
        });

        await pc.setLocalDescription(offer);

        socketRef.current.emit("offer", {
            target: targetId,
            sdp: offer,
        });
    };

    useEffect(() => {
        const socket = io(SIGNALING_URL, {
            path: "/socket.io",
            transports: ["websocket", "polling"],
            secure: window.location.protocol === "https:",
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 10000,
        });

        socketRef.current = socket;

        socket.on("connect", () => {
            setSocketId(socket.id);

            socket.emit("join-room", {
                roomId,
                role: "camera",
            });
        });

        socket.on("other-user", async ({ id }) => {
            setOtherUserId(id);
            remoteIdRef.current = id;

            await startCall(id);
        });

        socket.on("user-joined", ({ id }) => {
            setOtherUserId(id);
            remoteIdRef.current = id;
        });

        socket.on("offer", async ({ sdp, caller, iceRestart }) => {
            setOtherUserId(caller);
            remoteIdRef.current = caller;

            const pc = pcRef.current || (await createPeerConnection(caller));

            try {
                await pc.setRemoteDescription(new RTCSessionDescription(sdp));
            } catch (error) {
                const msg = String(error?.message || "");

                if (
                    msg.includes("The order of m-lines") ||
                    msg.includes("Failed to set remote offer sdp")
                ) {
                    console.warn("[Ignored WebRTC m-line order issue]", msg);
                    return;
                }

                throw error;
            }
            await flushPendingCandidates();

            const answer = await pc.createAnswer();

            await pc.setLocalDescription(answer);

            socket.emit("answer", {
                target: caller,
                sdp: answer,
                iceRestart,
            });
        });
        // ========================= PART 4 / 4 =========================

        socket.on("answer", async ({ sdp }) => {
            const pc = pcRef.current;

            if (!pc) return;

            if (pc.signalingState === "stable") return;

            await pc.setRemoteDescription(new RTCSessionDescription(sdp));

            await flushPendingCandidates();
        });

        socket.on("ice-candidate", async ({ candidate }) => {
            if (!candidate) return;

            const pc = pcRef.current;

            const ice = new RTCIceCandidate(candidate);

            if (pc && pc.remoteDescription) {
                try {
                    await pc.addIceCandidate(ice);
                } catch (error) {
                    addEvent("ICE ADD ERROR", error.message);
                }
            } else {
                pendingCandidatesRef.current.push(ice);
            }
        });

        socket.on("disconnect", (reason) => {
            addEvent("SIGNALING DISCONNECTED", reason);
        });

        socket.on("connect_error", (error) => {
            addEvent("SIGNALING ERROR", error.message);
        });

        return () => {
            closePeerConnection();

            socketRef.current?.disconnect();
            socketRef.current = null;

            localStreamRef.current?.getTracks().forEach((track) => {
                track.stop();
            });

            localStreamRef.current = null;
        };
    }, [roomId]);

    const summary = useMemo(() => {
        const totalOutage = losses.reduce((sum, item) => sum + item.durationMs, 0);

        return {
            samples: history.length,

            score: history.length ? Math.round(avg(history, "score")) : 0,

            avgUpload: avg(history, "uploadKbps"),
            avgDownload: avg(history, "downloadKbps"),

            peakUpload: max(history, "uploadKbps"),
            peakDownload: max(history, "downloadKbps"),

            avgLoss: avg(history, "packetLossPercent"),
            worstLoss: max(history, "packetLossPercent"),

            avgJitter: avg(history, "jitter"),
            worstJitter: max(history, "jitter"),

            avgRtt: avg(history, "rtt"),
            worstRtt: max(history, "rtt"),

            losses: losses.length,
            totalOutage,
        };
    }, [history, losses]);

    const realMediaFlow =
        connectionState === "connected" &&
        ["connected", "completed"].includes(iceState) &&
        stats.framesReceived > 0;

    return (
        <div style={styles.page}>
            {fullscreenTarget && (
                <div style={styles.fullscreenOverlay}>
                    <video
                        ref={fullscreenVideoRef}
                        autoPlay
                        playsInline
                        muted={fullscreenTarget === "local"}
                        style={styles.fullscreenVideo}
                    />
                </div>
            )}

            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>WebRTC Professional Monitoring Console</h1>

                    <p style={styles.subtitle}>
                        انتخاب میکروفون، اشتراک صفحه همراه با میکروفون انتخاب‌شده، اتصال مجدد نامحدود، حفظ Screen Share بعد از reconnect، مانیتورینگ حرفه‌ای و خروجی CSV.
                    </p>
                </div>

                <div style={styles.actions}>
                    <button
                        style={styles.primaryButton}
                        disabled={isCapturing}
                        onClick={startCapture}
                    >
                        Start Capture
                    </button>

                    <button
                        style={styles.dangerButton}
                        disabled={!isCapturing}
                        onClick={stopCapture}
                    >
                        Stop
                    </button>

                    <button
                        style={styles.secondaryButton}
                        onClick={() => exportCSV(history, "webrtc_stats")}
                    >
                        Export Stats CSV
                    </button>

                    <button
                        style={styles.secondaryButton}
                        onClick={() => exportCSV(events, "webrtc_events")}
                    >
                        Export Events CSV
                    </button>

                    <button
                        style={styles.secondaryButton}
                        onClick={() => exportCSV(hiccups, "webrtc_hiccups")}
                    >
                        Export Hiccups CSV
                    </button>
                </div>
            </div>

            <div style={styles.controlPanel}>
                <div>
                    <label style={styles.label}>Microphone / میکروفون</label>

                    <select
                        style={styles.select}
                        value={selectedMicId}
                        onChange={(event) => switchMicrophone(event.target.value)}
                    >
                        {microphones.map((microphone, index) => (
                            <option key={microphone.deviceId} value={microphone.deviceId}>
                                {microphone.label || `Microphone ${index + 1}`}
                            </option>
                        ))}
                    </select>
                </div>

                <button style={styles.secondaryButton} onClick={refreshMicrophones}>
                    Refresh Microphones
                </button>
            </div>

            <div style={styles.connectionBanner}>
                <strong>وضعیت واقعی اتصال:</strong>

                <span style={{ color: realMediaFlow ? "#86efac" : "#fca5a5" }}>
          {realMediaFlow
              ? "اتصال واقعی برقرار است و فریم از کلاینت مقابل دریافت می‌شود."
              : "اتصال رسانه‌ای واقعی تأیید نشده است یا فریم از کلاینت مقابل دریافت نمی‌شود."}
        </span>
            </div>

            <div style={styles.videoGrid}>
                <div style={styles.videoCard}>
                    <div style={styles.videoHeader}>
                        <div>
                            <h3 style={styles.cardTitle}>Local</h3>

                            <p style={styles.cardSubtitle}>
                                {localMode === "screen"
                                    ? "Screen Share + Selected Mic"
                                    : "Camera + Selected Mic"}
                            </p>
                        </div>

                        <div style={styles.videoActions}>
                            <button
                                style={styles.smallButton}
                                onClick={() => setFullscreenTarget("local")}
                            >
                                Fullscreen
                            </button>

                            <button style={styles.smallButton} onClick={switchToCamera}>
                                Camera
                            </button>

                            <button style={styles.smallButton} onClick={switchToScreen}>
                                Share Screen + Mic
                            </button>
                        </div>
                    </div>

                    <video
                        ref={localVideoRef}
                        autoPlay
                        playsInline
                        muted
                        style={styles.video}
                    />
                </div>

                <div style={styles.videoCard}>
                    <div style={styles.videoHeader}>
                        <div>
                            <h3 style={styles.cardTitle}>Remote</h3>

                            <p style={styles.cardSubtitle}>{otherUserId || "waiting..."}</p>
                        </div>

                        <button
                            style={styles.smallButton}
                            onClick={() => setFullscreenTarget("remote")}
                        >
                            Fullscreen
                        </button>
                    </div>

                    <video
                        ref={remoteVideoRef}
                        autoPlay
                        playsInline
                        style={styles.video}
                    />
                </div>
            </div>

            {!isCapturing && !history.length ? (
                <div style={styles.emptyHero}>
                    مانیتورینگ خاموش است. تماس و ویدیو می‌تواند فعال باشد، اما هیچ آماری تا زمان زدن Start Capture جمع‌آوری نمی‌شود.
                </div>
            ) : (
                <>
                    <div style={styles.statusGrid}>
                        <Status title="Capture" value={isCapturing ? "Recording" : "Stopped"} />
                        <Status title="Socket" value={socketId || "connecting"} />
                        <Status title="Peer" value={otherUserId || "waiting"} />
                        <Status title="WebRTC" value={connectionState} />
                        <Status title="ICE" value={iceState} />
                        <Status title="Signaling" value={signalingState} />
                        <Status title="TURN" value={stats.usingTurn ? "YES / relay" : "NO"} />
                        <Status title="Candidate Pair" value={stats.candidatePair} />
                        <Status title="Real Media Flow" value={realMediaFlow ? "YES" : "NO"} />
                        <Status title="Remote Frames" value={stats.framesReceived} />
                        <Status title="Reconnect Attempts" value={reconnectAttemptRef.current} />
                    </div>

                    <Panel title="Decision Gauges / شاخص‌های تصمیم‌گیری">
                        <div style={styles.gaugeGrid}>
                            <Gauge
                                label="Quality Score"
                                value={stats.score || summary.score}
                                max={100}
                                suffix="/100"
                            />

                            <Gauge
                                label="Packet Loss"
                                value={stats.packetLossPercent}
                                max={10}
                                suffix="%"
                                reverse
                            />

                            <Gauge
                                label="Jitter"
                                value={stats.jitter}
                                max={120}
                                suffix="ms"
                                reverse
                            />

                            <Gauge
                                label="RTT"
                                value={stats.rtt}
                                max={500}
                                suffix="ms"
                                reverse
                            />

                            <Gauge
                                label="FPS"
                                value={stats.fps || 0}
                                max={30}
                                suffix="fps"
                            />

                            <Gauge
                                label="Remote Frames"
                                value={stats.framesReceived}
                                max={Math.max(100, max(history, "framesReceived"))}
                                suffix=""
                            />
                        </div>
                    </Panel>

                    <Panel title="Professional Bars / شاخص‌های نواری">
                        <Bar label="Quality Score" value={stats.score} max={100} unit="/100" />
                        <Bar label="Upload" value={stats.uploadKbps} max={Math.max(1000, summary.peakUpload)} unit="kbps" />
                        <Bar label="Download" value={stats.downloadKbps} max={Math.max(1000, summary.peakDownload)} unit="kbps" />
                        <Bar label="Packet Loss" value={stats.packetLossPercent} max={10} unit="%" danger />
                        <Bar label="Jitter" value={stats.jitter} max={120} unit="ms" danger />
                        <Bar label="RTT" value={stats.rtt} max={500} unit="ms" danger />
                        <Bar label="FPS" value={stats.fps || 0} max={30} unit="fps" />
                        <Bar label="Frames Received" value={stats.framesReceived} max={Math.max(100, max(history, "framesReceived"))} unit="frames" />
                        <Bar label="Frames Dropped" value={stats.framesDropped} max={Math.max(20, max(history, "framesDropped"))} unit="frames" danger />
                        <Bar label="Freeze Count" value={stats.freezeCount} max={10} unit="freezes" danger />
                    </Panel>

                    <Panel title="Live Professional Stats / آمار زنده">
                        <div style={styles.statsGrid}>
                            <Stat label="Quality" value={stats.quality} />
                            <Stat label="Bytes Sent" value={formatBytes(stats.bytesSent)} />
                            <Stat label="Bytes Received" value={formatBytes(stats.bytesReceived)} />
                            <Stat label="Upload" value={`${stats.uploadKbps.toFixed(1)} kbps`} />
                            <Stat label="Download" value={`${stats.downloadKbps.toFixed(1)} kbps`} />
                            <Stat label="Packets Sent" value={stats.packetsSent} />
                            <Stat label="Packets Received" value={stats.packetsReceived} />
                            <Stat label="Packets Lost" value={stats.packetsLost} />
                            <Stat label="Packet Loss" value={`${stats.packetLossPercent.toFixed(2)}%`} />
                            <Stat label="Jitter" value={`${stats.jitter.toFixed(1)} ms`} />
                            <Stat label="RTT" value={`${stats.rtt.toFixed(1)} ms`} />
                            <Stat label="FPS" value={stats.fps || "—"} />
                            <Stat label="Resolution" value={stats.width && stats.height ? `${stats.width}×${stats.height}` : "—"} />
                            <Stat label="Frames Sent" value={stats.framesSent} />
                            <Stat label="Frames Received" value={stats.framesReceived} />
                            <Stat label="Frames Dropped" value={stats.framesDropped} />
                            <Stat label="Frames Decoded" value={stats.framesDecoded} />
                            <Stat label="Freeze Count" value={stats.freezeCount} />
                            <Stat label="Pause Count" value={stats.pauseCount} />
                            <Stat label="NACK" value={stats.nackCount} />
                            <Stat label="PLI" value={stats.pliCount} />
                            <Stat label="FIR" value={stats.firCount} />
                            <Stat label="Outgoing Capacity" value={`${stats.outgoingKbps.toFixed(1)} kbps`} />
                            <Stat label="Incoming Capacity" value={`${stats.incomingKbps.toFixed(1)} kbps`} />
                        </div>
                    </Panel>

                    <Panel title="Graphs / نمودارها">
                        <Graph title="Download kbps" data={history.map((item) => item.downloadKbps)} />
                        <Graph title="Upload kbps" data={history.map((item) => item.uploadKbps)} />
                        <Graph title="Packet Loss %" data={history.map((item) => item.packetLossPercent)} />
                        <Graph title="Jitter ms" data={history.map((item) => item.jitter)} />
                        <Graph title="RTT ms" data={history.map((item) => item.rtt)} />
                        <Graph title="Remote Frames" data={history.map((item) => item.framesReceived)} />
                        <Graph title="Frames Dropped" data={history.map((item) => item.framesDropped)} />
                        <Graph title="FPS" data={history.map((item) => item.fps || 0)} />
                    </Panel>

                    <Panel title="Captured Summary / خلاصه">
                        <div style={styles.statsGrid}>
                            <Stat label="Samples" value={summary.samples} />
                            <Stat label="Average Score" value={`${summary.score}/100`} />
                            <Stat label="Avg Upload" value={`${summary.avgUpload.toFixed(1)} kbps`} />
                            <Stat label="Avg Download" value={`${summary.avgDownload.toFixed(1)} kbps`} />
                            <Stat label="Peak Upload" value={`${summary.peakUpload.toFixed(1)} kbps`} />
                            <Stat label="Peak Download" value={`${summary.peakDownload.toFixed(1)} kbps`} />
                            <Stat label="Avg Loss" value={`${summary.avgLoss.toFixed(2)}%`} />
                            <Stat label="Worst Loss" value={`${summary.worstLoss.toFixed(2)}%`} />
                            <Stat label="Avg Jitter" value={`${summary.avgJitter.toFixed(1)} ms`} />
                            <Stat label="Worst Jitter" value={`${summary.worstJitter.toFixed(1)} ms`} />
                            <Stat label="Avg RTT" value={`${summary.avgRtt.toFixed(1)} ms`} />
                            <Stat label="Worst RTT" value={`${summary.worstRtt.toFixed(1)} ms`} />
                            <Stat label="Connection Losses" value={summary.losses} />
                            <Stat label="Total Lost Time" value={`${(summary.totalOutage / 1000).toFixed(1)}s`} />
                        </div>
                    </Panel>

                    <Panel title="Hiccups / اختلال‌های لحظه‌ای">
                        <Timeline items={hiccups} empty="No hiccups recorded." />
                    </Panel>

                    <Panel title="Connection Lost / قطعی اتصال">
                        <Timeline
                            items={losses.map((item) => ({
                                ...item,
                                type: "CONNECTION LOSS",
                                detail: `${item.reason}. Duration: ${(item.durationMs / 1000).toFixed(1)} seconds.`,
                            }))}
                            empty="No connection losses recorded."
                        />
                    </Panel>

                    <Panel title="Connection Events / رویدادها">
                        <Timeline items={events} empty="No events recorded." />
                    </Panel>
                </>
            )}
        </div>
    );
}

function Panel({ title, children }) {
    return (
        <section style={styles.panel}>
            <h3 style={styles.panelTitle}>{title}</h3>
            {children}
        </section>
    );
}

function Status({ title, value }) {
    return (
        <div style={styles.status}>
            <span style={styles.muted}>{title}</span>
            <strong>{value}</strong>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div style={styles.stat}>
            <span style={styles.muted}>{label}</span>
            <strong>{value}</strong>
        </div>
    );
}

function Gauge({ label, value, max, suffix = "", reverse = false }) {
    const rawPercent = Math.max(0, Math.min(100, (Number(value || 0) / max) * 100));
    const goodPercent = reverse ? 100 - rawPercent : rawPercent;

    const color =
        goodPercent > 70 ? "#22c55e" : goodPercent > 40 ? "#f59e0b" : "#ef4444";

    return (
        <div style={styles.gauge}>
            <div
                style={{
                    ...styles.gaugeRing,
                    background: `conic-gradient(${color} ${rawPercent}%, rgba(148,163,184,.18) 0)`,
                }}
            >
                <div style={styles.gaugeInner}>
                    {Number(value || 0).toFixed(value > 10 ? 0 : 1)}
                    {suffix}
                </div>
            </div>

            <strong>{label}</strong>
        </div>
    );
}
// ========================= PART 5 / 5 =========================

function Bar({ label, value, max, unit, danger = false }) {
    const percent = Math.max(0, Math.min(100, (Number(value || 0) / max) * 100));

    const color = danger
        ? percent > 60
            ? "#ef4444"
            : percent > 30
                ? "#f59e0b"
                : "#22c55e"
        : percent > 70
            ? "#22c55e"
            : percent > 35
                ? "#3b82f6"
                : "#f59e0b";

    return (
        <div style={styles.barBox}>
            <div style={styles.barTop}>
                <span>{label}</span>

                <strong>
                    {Number(value || 0).toFixed(1)} {unit}
                </strong>
            </div>

            <div style={styles.barTrack}>
                <div
                    style={{
                        ...styles.barFill,
                        width: `${percent}%`,
                        background: color,
                    }}
                />
            </div>
        </div>
    );
}

function Graph({ title, data }) {
    const values = data.slice(-80);
    const maxValue = Math.max(...values, 1);

    const points = values
        .map((value, index) => {
            const x = (index / Math.max(values.length - 1, 1)) * 300;
            const y = 80 - (Number(value || 0) / maxValue) * 70;

            return `${x},${y}`;
        })
        .join(" ");

    return (
        <div style={styles.graphCard}>
            <strong>{title}</strong>

            <svg viewBox="0 0 300 90" style={styles.svg}>
                <polyline
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    points={points}
                />
            </svg>
        </div>
    );
}

function Timeline({ items, empty }) {
    return (
        <div style={styles.timeline}>
            {items.map((item) => (
                <div key={item.id} style={styles.event}>
                    <div style={styles.eventTime}>{item.time}</div>

                    <div>
                        <strong>{item.type}</strong>

                        <p style={styles.eventText}>{item.detail}</p>
                    </div>
                </div>
            ))}

            {!items.length && <div style={styles.empty}>{empty}</div>}
        </div>
    );
}

const styles = {
    page: {
        minHeight: "100vh",
        padding: 32,
        background: "linear-gradient(135deg,#020617,#0f172a,#111827)",
        color: "#f8fafc",
        fontFamily: "Inter,system-ui,sans-serif",
    },

    fullscreenOverlay: {
        position: "fixed",
        inset: 0,
        zIndex: 999999,
        width: "100vw",
        height: "100vh",
        background: "#000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
        margin: 0,
        overflow: "hidden",
    },

    fullscreenVideo: {
        width: "100%",
        height: "100%",
        objectFit: "contain",
        background: "#000",
        display: "block",
    },

    header: {
        maxWidth: 1500,
        margin: "0 auto 24px",
        display: "flex",
        justifyContent: "space-between",
        gap: 20,
        flexWrap: "wrap",
    },

    title: {
        margin: 0,
        fontSize: 32,
        fontWeight: 800,
    },

    subtitle: {
        color: "#94a3b8",
        maxWidth: 950,
        lineHeight: 1.7,
    },

    actions: {
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
    },

    controlPanel: {
        maxWidth: 1500,
        margin: "0 auto 24px",
        padding: 18,
        borderRadius: 18,
        background: "rgba(15,23,42,.82)",
        border: "1px solid rgba(148,163,184,.22)",
        display: "flex",
        gap: 14,
        alignItems: "end",
        flexWrap: "wrap",
    },

    label: {
        display: "block",
        color: "#cbd5e1",
        marginBottom: 8,
        fontSize: 13,
    },

    select: {
        minWidth: 300,
        padding: "11px 12px",
        borderRadius: 12,
        background: "#020617",
        color: "#fff",
        border: "1px solid #475569",
    },

    primaryButton: {
        background: "#2563eb",
        color: "#fff",
        border: 0,
        borderRadius: 14,
        padding: "11px 16px",
        cursor: "pointer",
        fontWeight: 700,
    },

    dangerButton: {
        background: "#dc2626",
        color: "#fff",
        border: 0,
        borderRadius: 14,
        padding: "11px 16px",
        cursor: "pointer",
        fontWeight: 700,
    },

    secondaryButton: {
        background: "#334155",
        color: "#fff",
        border: 0,
        borderRadius: 14,
        padding: "11px 16px",
        cursor: "pointer",
        fontWeight: 700,
    },

    connectionBanner: {
        maxWidth: 1500,
        margin: "0 auto 24px",
        padding: 18,
        borderRadius: 18,
        background: "rgba(15,23,42,.82)",
        border: "1px solid rgba(148,163,184,.22)",
        display: "flex",
        gap: 12,
        flexWrap: "wrap",
    },

    videoGrid: {
        maxWidth: 1500,
        margin: "0 auto 24px",
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit,minmax(360px,1fr))",
        gap: 22,
    },

    videoCard: {
        borderRadius: 24,
        overflow: "hidden",
        background: "rgba(15,23,42,.78)",
        border: "1px solid rgba(148,163,184,.2)",
        boxShadow: "0 24px 60px rgba(0,0,0,.35)",
    },

    videoHeader: {
        padding: 18,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
    },

    cardTitle: {
        margin: 0,
    },

    cardSubtitle: {
        margin: "6px 0 0",
        color: "#94a3b8",
    },

    videoActions: {
        display: "flex",
        gap: 8,
        flexWrap: "wrap",
    },

    smallButton: {
        background: "#1e293b",
        color: "#fff",
        border: "1px solid #475569",
        borderRadius: 12,
        padding: "8px 12px",
        cursor: "pointer",
    },

    video: {
        width: "100%",
        aspectRatio: "16 / 10",
        background: "#000",
        objectFit: "contain",
        display: "block",
    },

    emptyHero: {
        maxWidth: 1500,
        margin: "0 auto",
        padding: 40,
        borderRadius: 24,
        background: "rgba(15,23,42,.78)",
        color: "#cbd5e1",
        textAlign: "center",
        lineHeight: 1.8,
    },

    statusGrid: {
        maxWidth: 1500,
        margin: "0 auto 24px",
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))",
        gap: 14,
    },

    status: {
        padding: 16,
        borderRadius: 18,
        background: "rgba(15,23,42,.78)",
        border: "1px solid rgba(148,163,184,.18)",
        overflow: "hidden",
    },

    panel: {
        maxWidth: 1500,
        margin: "0 auto 24px",
        padding: 22,
        borderRadius: 24,
        background: "rgba(15,23,42,.72)",
        border: "1px solid rgba(148,163,184,.18)",
    },

    panelTitle: {
        marginTop: 0,
    },

    statsGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
        gap: 14,
    },

    stat: {
        padding: 16,
        borderRadius: 16,
        background: "rgba(2,6,23,.55)",
        border: "1px solid rgba(148,163,184,.13)",
        overflow: "hidden",
    },

    muted: {
        display: "block",
        color: "#94a3b8",
        fontSize: 13,
        marginBottom: 6,
    },

    gaugeGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))",
        gap: 18,
    },

    gauge: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 12,
        padding: 18,
        borderRadius: 18,
        background: "rgba(2,6,23,.55)",
    },

    gaugeRing: {
        width: 140,
        height: 140,
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
    },

    gaugeInner: {
        width: 100,
        height: 100,
        borderRadius: "50%",
        background: "#020617",
        display: "grid",
        placeItems: "center",
        fontWeight: 800,
    },

    barBox: {
        padding: 16,
        borderRadius: 16,
        background: "rgba(2,6,23,.55)",
        border: "1px solid rgba(148,163,184,.13)",
        marginBottom: 14,
    },

    barTop: {
        display: "flex",
        justifyContent: "space-between",
        marginBottom: 8,
        color: "#e2e8f0",
    },

    barTrack: {
        height: 14,
        borderRadius: 999,
        background: "rgba(148,163,184,.18)",
        overflow: "hidden",
    },

    barFill: {
        height: "100%",
        borderRadius: 999,
    },

    graphCard: {
        padding: 16,
        borderRadius: 16,
        background: "rgba(2,6,23,.55)",
        border: "1px solid rgba(148,163,184,.13)",
        marginBottom: 14,
    },

    svg: {
        width: "100%",
        height: 90,
        color: "#60a5fa",
        marginTop: 8,
    },

    timeline: {
        display: "flex",
        flexDirection: "column",
        gap: 12,
    },

    event: {
        display: "grid",
        gridTemplateColumns: "110px 1fr",
        gap: 14,
        padding: 14,
        borderRadius: 16,
        background: "rgba(2,6,23,.55)",
    },

    eventTime: {
        color: "#93c5fd",
        fontWeight: 700,
    },

    eventText: {
        margin: "5px 0 0",
        color: "#cbd5e1",
        lineHeight: 1.6,
    },

    empty: {
        color: "#94a3b8",
        padding: 20,
        textAlign: "center",
    },
};
